import { useState, useEffect, useCallback, useRef } from 'react';
import { Bug, Lightbulb, Sparkles, Loader2, CheckCircle, CloudOff, Cloud } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogBody,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useSubmitTicket, TicketType } from '@/hooks/useSubmitTicket';

const STORAGE_KEY = 'mesterplan_feedback_draft';

interface FeedbackDraft {
    type: TicketType;
    title: string;
    description: string;
    stepsToReproduce: string;
    expectedBehavior: string;
    actualBehavior: string;
}

/**
 * A reason a person can read. The server sends Danish details, but a network failure surfaces
 * the browser's own English text ("Failed to fetch") and a proxy error page arrives as HTML.
 */
export function feedbackFailureReason(error: unknown): string {
    if (error instanceof TypeError) {
        return 'Ingen forbindelse til Mesterplan.';
    }
    const message = error instanceof Error ? error.message.trim() : '';
    if (!message || message.length > 150 || message.includes('<')) {
        return 'Der opstod en fejl hos os.';
    }
    return /[.!?]$/.test(message) ? message : `${message}.`;
}

interface FeedbackDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

interface TicketTypeOption {
    value: TicketType;
    label: string;
    description: string;
    icon: React.ReactNode;
}

const ticketTypes: TicketTypeOption[] = [
    {
        value: 'bug',
        label: 'Fejl',
        description: 'Rapporter en fejl',
        icon: <Bug className="h-4 w-4" />,
    },
    {
        value: 'feature',
        label: 'Ny funktion',
        description: 'Foreslå en ny funktion',
        icon: <Lightbulb className="h-4 w-4" />,
    },
    {
        value: 'enhancement',
        label: 'Forbedring',
        description: 'Foreslå en forbedring',
        icon: <Sparkles className="h-4 w-4" />,
    },
];

function loadDraft(): FeedbackDraft | null {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            return JSON.parse(saved);
        }
    } catch {
        // Ignore parse errors
    }
    return null;
}

function saveDraft(draft: FeedbackDraft): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
    } catch {
        // Ignore storage errors
    }
}

function clearDraft(): void {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Ignore storage errors
    }
}

function getBrowserInfo(): string {
    const ua = navigator.userAgent;
    let browser = 'Ukendt browser';

    if (ua.includes('Firefox/')) {
        const match = ua.match(/Firefox\/(\d+)/);
        browser = `Firefox ${match?.[1] || ''}`;
    } else if (ua.includes('Edg/')) {
        const match = ua.match(/Edg\/(\d+)/);
        browser = `Edge ${match?.[1] || ''}`;
    } else if (ua.includes('Chrome/')) {
        const match = ua.match(/Chrome\/(\d+)/);
        browser = `Chrome ${match?.[1] || ''}`;
    } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
        const match = ua.match(/Version\/(\d+)/);
        browser = `Safari ${match?.[1] || ''}`;
    }

    return browser;
}

function getDeviceInfo(): string {
    const ua = navigator.userAgent;

    if (/iPhone/.test(ua)) return 'iPhone';
    if (/iPad/.test(ua)) return 'iPad';
    if (/Android/.test(ua)) return 'Android';
    if (/Mac/.test(ua)) return 'Mac';
    if (/Windows/.test(ua)) return 'Windows PC';
    if (/Linux/.test(ua)) return 'Linux';

    return 'Ukendt enhed';
}

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
    // Load draft on initial mount
    const [type, setType] = useState<TicketType>(() => loadDraft()?.type ?? 'bug');
    const [title, setTitle] = useState(() => loadDraft()?.title ?? '');
    const [description, setDescription] = useState(() => loadDraft()?.description ?? '');
    const [stepsToReproduce, setStepsToReproduce] = useState(() => loadDraft()?.stepsToReproduce ?? '');
    const [expectedBehavior, setExpectedBehavior] = useState(() => loadDraft()?.expectedBehavior ?? '');
    const [actualBehavior, setActualBehavior] = useState(() => loadDraft()?.actualBehavior ?? '');
    const [showSuccess, setShowSuccess] = useState(false);
    const [issueNumber, setIssueNumber] = useState<number | null>(null);
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
    // A failed send used to only reach the console, so the tester believed it had gone through.
    const [submitError, setSubmitError] = useState<string | null>(null);
    // Cleared on close rather than on open, so a reopened dialog never flashes the old error.
    useEffect(() => {
        if (!open) setSubmitError(null);
    }, [open]);
    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const { mutate: submitTicket, isPending } = useSubmitTicket();

    // Auto-save draft when form changes (debounced)
    useEffect(() => {
        const hasContent = title || description || stepsToReproduce || expectedBehavior || actualBehavior;

        if (!hasContent) {
            setSaveStatus('idle');
            return;
        }

        // Show "saving" indicator after a short delay
        setSaveStatus('saving');

        // Clear any existing debounce timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        // Debounce the actual save operation
        debounceTimeoutRef.current = setTimeout(() => {
            saveDraft({
                type,
                title,
                description,
                stepsToReproduce,
                expectedBehavior,
                actualBehavior,
            });

            // Clear any existing save timeout
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }

            // Show "saved" status
            saveTimeoutRef.current = setTimeout(() => {
                setSaveStatus('saved');
            }, 200);
        }, 800); // Wait 800ms after user stops typing before saving

        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [type, title, description, stepsToReproduce, expectedBehavior, actualBehavior]);

    // Check if there's an existing draft on mount
    useEffect(() => {
        const draft = loadDraft();
        if (draft && (draft.title || draft.description)) {
            setSaveStatus('saved');
        }
    }, []);

    // Reset success state when dialog closes (but keep the draft)
    useEffect(() => {
        if (!open && showSuccess) {
            const timeout = setTimeout(() => {
                setShowSuccess(false);
                setIssueNumber(null);
            }, 300);
            return () => clearTimeout(timeout);
        }
    }, [open, showSuccess]);

    // Clear form and draft after successful submission
    const resetForm = useCallback(() => {
        setType('bug');
        setTitle('');
        setDescription('');
        setStepsToReproduce('');
        setExpectedBehavior('');
        setActualBehavior('');
        setSaveStatus('idle');
        clearDraft();
    }, []);

    const handleSubmit = () => {
        if (!title.trim() || !description.trim()) return;
        setSubmitError(null);

        submitTicket(
            {
                type,
                title: title.trim(),
                description: description.trim(),
                stepsToReproduce: type === 'bug' ? stepsToReproduce.trim() : undefined,
                expectedBehavior: type === 'bug' ? expectedBehavior.trim() : undefined,
                actualBehavior: type === 'bug' ? actualBehavior.trim() : undefined,
                browser: getBrowserInfo(),
                device: getDeviceInfo(),
            },
            {
                onSuccess: (result) => {
                    setIssueNumber(result.issueNumber);
                    setShowSuccess(true);
                    resetForm();
                },
                onError: (error) => {
                    console.error('Failed to submit ticket:', error);
                    // The form keeps its text, so pressing send again just works.
                    setSubmitError(
                        `Din feedback blev ikke sendt: ${feedbackFailureReason(error)} Teksten står her stadig, så du kan prøve igen om lidt — eller skrive til support@mesterplan.dk.`
                    );
                },
            }
        );
    };

    const isValid = title.trim() && description.trim();

    if (showSuccess) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-md">
                    <DialogBody className="text-center py-8">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-success/10 mb-4">
                            <CheckCircle className="h-8 w-8 text-success" />
                        </div>
                        <h3 className="text-17 font-semibold text-dark dark:text-dark-text mb-2">
                            Tak for din feedback!
                        </h3>
                        <p className="text-[14px] text-muted dark:text-dark-muted mb-4">
                            Din henvendelse er blevet registreret
                            {issueNumber && ` som sag #${issueNumber}`}.
                        </p>
                        <p className="text-[13px] text-muted dark:text-dark-muted">
                            Vi kigger på det hurtigst muligt.
                        </p>
                    </DialogBody>
                    <DialogFooter>
                        <Button onClick={() => onOpenChange(false)}>
                            Luk
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader onClose={() => onOpenChange(false)}>
                    <DialogTitle>Giv feedback</DialogTitle>
                    <DialogDescription>
                        Hjælp os med at forbedre MesterPlan
                    </DialogDescription>
                </DialogHeader>

                <DialogBody className="space-y-4">
                    {submitError && (
                        <div role="alert" className="p-3 rounded-md bg-error-light text-error text-sm">
                            {submitError}
                        </div>
                    )}

                    {/* Type selector */}
                    <div>
                        <label className="block text-[14px] font-medium text-dark dark:text-dark-text mb-2">
                            Type
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                            {ticketTypes.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => setType(option.value)}
                                    className={`
                                        flex flex-col items-center gap-1.5 p-3 rounded-lg border transition-all
                                        ${type === option.value
                                            ? 'border-primary bg-primary/5 text-primary dark:text-primary-400'
                                            : 'border-bordergray dark:border-dark-border text-muted dark:text-dark-muted hover:border-primary/50 hover:bg-surface dark:hover:bg-dark-hover'
                                        }
                                    `}
                                >
                                    {option.icon}
                                    <span className="text-[13px] font-medium">{option.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Title */}
                    <Input
                        label="Titel"
                        placeholder={
                            type === 'bug'
                                ? 'F.eks. "Kan ikke gemme projekt"'
                                : type === 'feature'
                                ? 'F.eks. "Integration med e-conomic"'
                                : 'F.eks. "Bedre filtrering på projekter"'
                        }
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                    />

                    {/* Description */}
                    <Textarea
                        label="Beskrivelse"
                        placeholder={
                            type === 'bug'
                                ? 'Beskriv hvad der går galt...'
                                : type === 'feature'
                                ? 'Beskriv den ønskede funktion...'
                                : 'Beskriv hvad du gerne vil have forbedret...'
                        }
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="min-h-[80px]"
                    />

                    {/* Bug-specific fields */}
                    {type === 'bug' && (
                        <>
                            <Textarea
                                label="Trin til at genskabe fejlen"
                                placeholder="1. Gå til...&#10;2. Klik på...&#10;3. Se fejlen"
                                value={stepsToReproduce}
                                onChange={(e) => setStepsToReproduce(e.target.value)}
                                className="min-h-[80px]"
                            />
                            <div className="grid grid-cols-2 gap-3">
                                <Textarea
                                    label="Forventet adfærd"
                                    placeholder="Hvad burde der ske?"
                                    value={expectedBehavior}
                                    onChange={(e) => setExpectedBehavior(e.target.value)}
                                    className="min-h-[60px]"
                                />
                                <Textarea
                                    label="Faktisk adfærd"
                                    placeholder="Hvad sker der i stedet?"
                                    value={actualBehavior}
                                    onChange={(e) => setActualBehavior(e.target.value)}
                                    className="min-h-[60px]"
                                />
                            </div>
                        </>
                    )}

                    {/* Auto-detected info and save status */}
                    <div className="flex items-center justify-between text-[12px] text-muted dark:text-dark-muted pt-2 border-t border-bordergray dark:border-dark-border">
                        <div className="flex gap-4">
                            <span>Browser: {getBrowserInfo()}</span>
                            <span>Enhed: {getDeviceInfo()}</span>
                        </div>
                        {saveStatus !== 'idle' && (
                            <div className="flex items-center gap-1.5 text-muted dark:text-dark-muted">
                                {saveStatus === 'saving' ? (
                                    <>
                                        <CloudOff className="h-3.5 w-3.5 animate-pulse" />
                                        <span>Gemmer...</span>
                                    </>
                                ) : (
                                    <>
                                        <Cloud className="h-3.5 w-3.5 text-success" />
                                        <span className="text-success">Gemt</span>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </DialogBody>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Annuller
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!isValid || isPending}
                    >
                        {isPending ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Sender...
                            </>
                        ) : (
                            'Send feedback'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
